// Core imports
import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Space, Col, Modal, Row, Typography, Grid, Radio, Empty, Spin } from 'antd';

// Misc imports
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import { NAV_ORGANIZATION_KNOWLEDGE_BASE_NEW } from '../../constants/navigation';
import { useNavigate, useParams } from 'react-router-dom';
import { useFetchIntegrationsQuery } from '../../store/integrations/integrationsApi';
import { LoadingOutlined } from '@ant-design/icons';
import IntegrationForm from '../Forms/IntegrationForm';
import { INTEGRATION_CATEGORY, INTEGRATION_TYPE, INTEGRATIONS } from '../../constants/integrations';

// Constant declarations
const { useBreakpoint } = Grid;
const { Text, Paragraph } = Typography;

const AddNewKnowledgeBaseModal = ({
  isModalOpen,
  closeModal
}) => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const isMobileSize = (screens.xs || screens.sm || screens.md) && !screens.lg;
  const navigate = useNavigate();
  const { data: integrations, isLoading: isLoadingIntegrations } = useFetchIntegrationsQuery({ orgId, urlParams: { category: INTEGRATION_CATEGORY.KB, sortBy: 'createdAt', order: 'DESC' } });

  const [isIntegrateMode, setIsIntegrateMode] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState();
  const [addNewIntegration, setAddNewIntegration] = useState(false);

  const onCloseModal = () => {
    setIsIntegrateMode(false);
    setSelectedIntegration(null);
    setAddNewIntegration(false);
    closeModal();
  };

  const handleOnSuccess = (response) => {
    setSelectedIntegration(response);
    setAddNewIntegration(false);
  };

  const renderInitOptions = () => {
    return (
      <Row justify={'center'} gutter={48}>
        <Col span={isMobileSize ? 24 : 12}>
          <Row>
            <Col span={24} style={{ width: isMobileSize ? '100%' : '250px' }}>
              <Row justify={'center'}>
                <Button
                  onClick={() => setIsIntegrateMode(true)}
                  type="text"
                  className="bg-layout w-100"
                  style={{ height: '100px' }}
                >
                  <Text className="fw-bold">{t('Integration')}</Text>
                </Button>
              </Row>
            </Col>

            <Col span={24} className="mt-3 mb-5">
              <Text className="fs-sm text-muted mt-5">{t('Use the available integrations to automatically upload content.')}</Text>
            </Col>
          </Row>
        </Col>

        <Col span={isMobileSize ? 24 : 12}>
          <Row >
            <Col span={24} style={{ width: isMobileSize ? '100%' : '250px' }}>
              <Row justify={'end'}>
                <Button
                  onClick={() => {
                    navigate(`${replaceUrlParams(NAV_ORGANIZATION_KNOWLEDGE_BASE_NEW, { orgId: orgId })}`);
                    onCloseModal();
                  }}
                  type="text"
                  className="bg-layout w-100"
                  style={{ height: '100px' }}
                >
                  <Text className="fw-bold">{t('Internal / Manual upload')}</Text>
                </Button>
              </Row>
            </Col>

            <Col span={24} className="mt-3 mb-5">
              <Text className="fs-sm text-muted mt-2">{t('Import articles manually via CSV or add individual articles through the build-in editor.')}</Text>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  const renderIntegrationImport = () => {
    return (
      <Row>
        <Fragment>
          {
            isLoadingIntegrations ? (
              <Spin indicator={<LoadingOutlined className="fs-xxl" spin />} />
            ) : (
              addNewIntegration ? (
                <div
                  className="overflow-y-auto overflow-x-hidden scrollbar-custom w-100"
                  style={{ height: 550 }}
                >
                  <IntegrationForm
                    integration={{
                      type: INTEGRATION_TYPE.MICROSOFT_DYNAMICS_KB,
                      category: INTEGRATION_CATEGORY.KB
                    }}
                    onSuccess={handleOnSuccess}
                    onCancel={onCloseModal}
                  />
                </div>
              ) : (
                integrations?.items?.length > 0 ? (
                  <Radio.Group
                    value={selectedIntegration ? integrations.items.find(item => item.id === selectedIntegration?.id) : null}
                    onChange={(e) => {
                      setSelectedIntegration(e.target.value);
                    }}
                    className="overflow-y-auto overflow-x-hidden scrollbar-custom w-100"
                    style={{ minHeight: 150, maxHeight: 350 }}
                  >
                    <Space direction="vertical" >
                      {integrations?.items?.map((integration) => {
                        return (
                          <Radio value={integration} key={integration.id}>
                            <Space>
                              {INTEGRATIONS.find(i => i.type === integration?.type)?.renderTypeIcon({ className: 'fs-xxxl' })}
                              {integration.name}
                            </Space>
                          </Radio>
                        );
                      })}
                    </Space>
                  </Radio.Group>
                ) : (
                  <Empty
                    description={<Text type="secondary">{t('There are no KB integrations available')}.</Text>}
                  />
                )
              )
            )
          }
        </Fragment>
      </Row>
    );
  };

  const renderFooter = () => {
    return (
      <Row justify="end" gutter={16}>
        <Col>
          <Button type="text" size="large" className="text-primary" onClick={() => setAddNewIntegration(true)}>
            {t('Configure New Integration')}
          </Button>
        </Col>

        <Col>
          <Button disabled={!selectedIntegration} type="primary" size="large" onClick={() => {
            const urlSearchParams = new URLSearchParams({ integrationId: selectedIntegration.id });
            navigate({
              pathname: replaceUrlParams(NAV_ORGANIZATION_KNOWLEDGE_BASE_NEW,
                { orgId: orgId }),
              search: urlSearchParams.toString()
            });
            onCloseModal();
          }}>
            {t('Integrate / Import KB')}
          </Button>
        </Col>
      </Row>
    );
  };

  return (
    <Modal
      title={
        <Fragment>
          <Paragraph className="d-flex mb-0">
            {!isIntegrateMode ? t('Choose KB type') : t('Import KB with Integration')}
          </Paragraph>
          <Paragraph className="fs-lg fw-normal text-muted mb-0 mt-2">
            {!isIntegrateMode ? t('Choose how you would like to import the articles of your Knowledge Base') : t('Choose one of available KB Organization\'s Integrations or add a new one.')}
          </Paragraph>
        </Fragment>
      }
      open={isModalOpen}
      onCancel={onCloseModal}
      centered
      footer={isIntegrateMode && !addNewIntegration ? renderFooter() : null}
      width={ isMobileSize ? '95%' : '45%'}
      destroyOnClose
    >
      {!isIntegrateMode ? renderInitOptions() : renderIntegrationImport()}
    </Modal>
  );
};

AddNewKnowledgeBaseModal.propTypes = {
  isModalOpen: PropTypes.bool,
  closeModal: PropTypes.func
};
export default AddNewKnowledgeBaseModal;
